import React, { useEffect, useState } from 'react';
import {
  CircularProgress,
  Alert,
  Box,
  Chip,
  Paper,
  Typography,
  Tabs,
  Tab,
  LinearProgress,
} from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid';
import { DataGrid, PageTitle, ListLayoutPanel, MainSectionPanel } from '../../../components/common';
import { devolucionInternaService } from './services/devolucionInternaService';
import type { DevolucionInternaItem, DevolucionInternaResumen, EstadoDevolucion } from './types';
import { getEstadoDevolucion } from './types';

const estadoColors: Record<EstadoDevolucion, 'info' | 'warning' | 'success'> = {
  'Disponible': 'info',
  'Parcial': 'warning',
  'Consumido': 'success',
};

const MetricCard: React.FC<{ label: string; value: string; color: string }> = ({ label, value, color }) => (
  <Paper
    sx={{
      p: 2,
      flex: 1,
      textAlign: 'center',
      borderTop: 3,
      borderColor: `${color}.main`,
    }}
    elevation={1}
  >
    <Typography variant="body2" color="text.secondary" gutterBottom>
      {label}
    </Typography>
    <Typography variant="h5" fontWeight={700} color={`${color}.main`}>
      {value}
    </Typography>
  </Paper>
);

const DevolucionesInternasPage: React.FC = () => {
  const [items, setItems] = useState<DevolucionInternaItem[]>([]);
  const [resumen, setResumen] = useState<DevolucionInternaResumen | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const report = await devolucionInternaService.getReport();
      setItems(report.items);
      setResumen(report.resumen);
    } catch (err) {
      console.error('Error loading devoluciones internas:', err);
      setError('Error al cargar las devoluciones internas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredItems = items.filter((item) => {
    const estado = getEstadoDevolucion(item);
    switch (selectedTab) {
      case 0: return estado === 'Disponible';
      case 1: return estado === 'Parcial';
      case 2: return estado === 'Consumido';
      case 3: return true; // Todos
      default: return true;
    }
  });

  const countByEstado = {
    todos: items.length,
    disponible: items.filter(i => getEstadoDevolucion(i) === 'Disponible').length,
    parcial: items.filter(i => getEstadoDevolucion(i) === 'Parcial').length,
    consumido: items.filter(i => getEstadoDevolucion(i) === 'Consumido').length,
  };

  const columns: GridColDef[] = [
    {
      field: 'numeroDerc',
      headerName: 'DERC',
      width: 120,
    },
    {
      field: 'numeroGuiaProduccion',
      headerName: 'N° Guía',
      width: 90,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'descripcionArticulo',
      headerName: 'Artículo',
      flex: 1,
      minWidth: 250,
    },
    {
      field: 'presentacion',
      headerName: 'Presentación',
      width: 150,
    },
    {
      field: 'calibre',
      headerName: 'Calibre',
      width: 100,
    },
    {
      field: 'cantidadDevolucionInterna',
      headerName: 'Cant.',
      width: 80,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'cantidadUsada',
      headerName: 'Usada',
      width: 80,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'cantidadDisponible',
      headerName: 'Disp.',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value > 0 ? 'info' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'pesoTotal',
      headerName: 'Peso Total',
      width: 110,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => `${params.value.toFixed(2)} kg`,
    },
    {
      field: 'pesoUsado',
      headerName: 'Peso Usado',
      width: 110,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => `${params.value.toFixed(2)} kg`,
    },
    {
      field: 'pesoDisponible',
      headerName: 'Peso Disp.',
      width: 110,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography
          variant="body2"
          fontWeight={params.value > 0 ? 600 : 400}
          color={params.value > 0 ? 'primary' : 'text.secondary'}
        >
          {params.value.toFixed(2)} kg
        </Typography>
      ),
    },
    {
      field: 'porcentaje',
      headerName: 'Uso',
      width: 120,
      renderCell: (params) => {
        const item = params.row as DevolucionInternaItem;
        const porcentaje = item.cantidadDevolucionInterna > 0
          ? (item.cantidadUsada / item.cantidadDevolucionInterna) * 100
          : 0;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <LinearProgress
              variant="determinate"
              value={Math.min(porcentaje, 100)}
              sx={{ flex: 1, height: 8, borderRadius: 4 }}
              color={porcentaje >= 100 ? 'success' : porcentaje > 0 ? 'warning' : 'info'}
            />
            <Typography variant="caption" sx={{ minWidth: 35 }}>
              {porcentaje.toFixed(0)}%
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const item = params.row as DevolucionInternaItem;
        const estado = getEstadoDevolucion(item);
        return (
          <Chip
            label={estado}
            size="small"
            color={estadoColors[estado]}
          />
        );
      },
    },
  ];

  return (
    <MainSectionPanel>
      <ListLayoutPanel>
        <PageTitle
          title="Devoluciones Internas"
          subtitle="Seguimiento de productos con devolución interna"
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Metric Cards */}
        {resumen && (
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <MetricCard
              label="Peso Disponible"
              value={`${resumen.pesoTotalDisponible.toFixed(2)} kg`}
              color="success"
            />
            <MetricCard
              label="Peso Usado"
              value={`${resumen.pesoTotalUsado.toFixed(2)} kg`}
              color="warning"
            />
            <MetricCard
              label="Total Devolución"
              value={`${resumen.pesoTotalDevolucion.toFixed(2)} kg`}
              color="primary"
            />
          </Box>
        )}

        {/* Tabs */}
        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => setSelectedTab(newValue)}
          sx={{ mb: 2 }}
        >
          <Tab label={`Disponible (${countByEstado.disponible})`} />
          <Tab label={`Parcial (${countByEstado.parcial})`} />
          <Tab label={`Consumido (${countByEstado.consumido})`} />
          <Tab label={`Todos (${countByEstado.todos})`} />
        </Tabs>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={filteredItems}
            columns={columns}
            getRowId={(row) => row.id}
            pageSize={25}
            height={600}
          />
        )}
      </ListLayoutPanel>
    </MainSectionPanel>
  );
};

export default DevolucionesInternasPage;
